import { MapsWidget } from '@/components/MapsWidget';
import { BuscaPassagensWidget } from '@/components/BuscaPassagensWidget';
import { PromptAlignedFAQ } from '@/components/PromptAlignedFAQ';

export const frontmatter = {
  category: 'guia',
  title: 'Como Ir de Curitiba para Ilha do Mel: Carro, Ônibus e Tour',
  excerpt:
    'Passo a passo completo de como ir de Curitiba para a Ilha do Mel: de carro pela BR-277, de ônibus até Paranaguá, de trem pela Serra Verde Express ou em tour de 1 dia. Com preços e horários 2026.',
  publishedAt: '2026-05-17',
  author: 'Guia Ilha do Mel',
  tags: ['curitiba', 'como-chegar', 'onibus', 'trem', 'serra-verde-express', 'tour'],
  coverImage: '/images/sailboat.jpg',
  readingTime: '8 min',
};

# Como Ir de Curitiba para [Ilha do Mel](/): Carro, Ônibus e Tour Completo

**Como ir de Curitiba para a Ilha do Mel?** Existem quatro opções principais: de carro pela BR-277, de ônibus até Paranaguá ou Pontal do Sul, de trem pela Serra Verde Express (a opção mais bonita), ou em um tour organizado de 1 dia. Neste guia, detalhamos cada rota com passo a passo, preços estimados e dicas para não errar.

<MapsWidget query="Curitiba PR para Ilha do Mel, Paranaguá PR" />

---

## Opção 1: De Carro (Rota Completa com Pedágios)

O carro é a opção mais flexível e permite parar no caminho, guardar bagagem no porta-malas e escolher o horário de saída. É a escolha de 70% dos turistas que visitam a ilha.

### Passo a passo de carro

**1. Saia de Curitiba pela BR-277 sentido leste**

- A BR-277 é a Rodovia do Café, também chamada de Rodovia do Mar neste trecho
- Saída fácil a partir do Contorno Leste (Bairro Cajuru ou Pinheirinho)

**2. Percorra ~70 km até o trevo de acesso a Paranaguá**

- Trecho com belas vistas da Serra do Mar — especialmente a descida pela BR-277, com viadutos e mirantes
- Evite viajar com neblina forte: a descida da serra pode ser perigosa

**3. Após Paranaguá, siga pela PR-407 (Estrada do Mel) por ~30 km até Pontal do Sul**

- Estrada reta, bem sinalizada, sem mais pedágios nesse trecho
- A PR-407 corta o litoral norte paranaense

**4. Chegue ao Terminal de Embarque de Pontal do Sul**

- Endereço: Rua Alameda do Café, Balneário Pontal do Sul
- Estacione em um dos estacionamentos pagos próximos (R$ 25–40/diária)

**5. Compre o bilhete da barca ou táxi náutico**

- Barca: R$ 44,18 ida e volta (2025) — saídas a cada 30 min (alta temporada)
- Táxi náutico: mais rápido (~15 min), mais caro (~R$ 60–80 por pessoa)

**6. Escolha o destino na ilha: Nova Brasília ou Encantadas**

- As linhas são separadas — escolha antes de comprar o bilhete
- Se errar o trapiche, terá que pagar táxi náutico interno (~R$ 15–25)

### Resumo do carro

| Informação                         | Dado            |
| ---------------------------------- | --------------- |
| Distância Curitiba → Pontal do Sul | ~120 km         |
| Tempo de carro                     | 1h30 a 1h45     |
| Pedágios estimados                 | R$ 20–30 (ida)  |
| Estacionamento em Pontal           | R$ 25–40/diária |
| Travessia de barco                 | 30–35 min       |
| Custo do barco (ida e volta)       | ~R$ 44,18       |

---

## Opção 2: De Ônibus (Sem Carro)

Ir sem carro é totalmente viável. O roteiro de ônibus tem duas etapas: Curitiba → Paranaguá (ou Pontal do Sul), depois barco até a ilha.

### Etapa 1: Curitiba → Paranaguá

**Terminal Rodoviário de Curitiba (Rodoferroviária)**

- Localização: Av. Presidente Affonso Camargo, 330 — Centro Cívico, Curitiba
- Empresas: Viação Graciosa, Auto Viação São José
- Frequência: várias saídas por dia (consulte o site Clickbus ou a rodoviária)
- Duração: ~1h30 de viagem
- Preço: ~R$ 30–45 (2026)

<BuscaPassagensWidget origem="Curitiba" destino="Paranaguá" />

### Etapa 2: Paranaguá → Ilha do Mel

No centro de Paranaguá, próximo ao Mercado do Café:

- **Saídas**: Seg–Sex às 15h30 / Sáb–Dom às 9h30 (apenas 1 saída por dia em cada período)
- **Travessia**: 1h45 de barco
- **Preço**: ~R$ 76,50 ida e volta (2025)
- **Vantagem**: a travessia passa por toda a Baía de Paranaguá — é cênica e memorável

> **Dica prática:** se for de ônibus, chegue a Paranaguá cedo e aproveite para almoçar no Mercado do Café (barreado, frutos do mar) antes de pegar o barco das 15h30.

---

## Opção 3: De Trem — Serra Verde Express

Esta é, disparado, a **opção mais bonita** para ir da capital ao litoral paranaense. O trem turístico Serra Verde Express faz o percurso Curitiba → Morretes → Paranaguá com paisagens espetaculares da Serra do Mar.

### Informações do trem

| Informação       | Detalhe                                                                |
| ---------------- | ---------------------------------------------------------------------- |
| Rota             | Curitiba → Morretes → Paranaguá                                        |
| Duração          | ~4h (Curitiba → Paranaguá)                                             |
| Partida          | Estação Ferroviária de Curitiba (Rodoferroviária)                      |
| Horário          | Verificar no site da Serra Verde Express (varia por temporada)         |
| Preço            | A partir de R$ 120 por trecho (classes variam)                         |
| Atrações da rota | 13 pontes, 7 viadutos, Floresta de Araucárias, descida da Serra do Mar |

### O percurso de trem

O trem sobe a Serra do Mar no sentido inverso — parte do planalto curitibano e desce até o litoral:

- **Curitiba → Morretes**: trecho mais dramático, com a descida da Serra do Mar por viadutos e pontes centenárias. Vista da floresta de Araucárias abrindo para a Mata Atlântica fechada
- **Morretes**: cidade histórica conhecida pelo barreado (prato típico). Vale parar aqui para almoçar se você não for até Paranaguá
- **Paranaguá**: fim da linha. De lá, você pega o barco para a Ilha do Mel

### Roteiro de 2 dias com o trem

- **Dia 1 (manhã)**: Trem Curitiba → Paranaguá (4h)
- **Dia 1 (tarde)**: Barco Paranaguá → Ilha do Mel (1h45) — embarque às 15h30
- **Dia 2 ou 3**: Retorno da ilha de barco + ônibus ou carro alugado em Paranaguá

> A Serra Verde Express tem site oficial para compra de passagens. Recomenda-se reservar com antecedência, especialmente nos fins de semana e feriados.

---

## Opção 4: Tour de 1 Dia Saindo de Curitiba

Para quem tem pouco tempo ou não quer se preocupar com logística, os **tours de 1 dia** são uma boa solução. Diversas agências de Curitiba oferecem o pacote completo.

### O que geralmente inclui

- Transfer Curitiba → Pontal do Sul em van ou microônibus
- Passagem de barco ida e volta
- Guia turístico local
- Almoço (em alguns pacotes)
- Retorno à Curitiba no final da tarde

### Prós e contras

|                     | Prós                                                | Contras                           |
| ------------------- | --------------------------------------------------- | --------------------------------- |
| **Tour organizado** | Sem preocupação com logística, guia incluído        | Pouco tempo na ilha, horário fixo |
| **Carro próprio**   | Flexibilidade total, pode ir e voltar quando quiser | Precisa estacionar, pagar pedágio |
| **Ônibus**          | Mais econômico, sem precisar de carro               | Menos flexibilidade de horário    |
| **Trem**            | Experiência inesquecível, paisagem única            | Mais longo, preço mais alto       |

---

## Qual Opção Escolher?

| Perfil de viajante                        | Melhor opção                                |
| ----------------------------------------- | ------------------------------------------- |
| Família com crianças pequenas             | Carro próprio                               |
| Mochileiro / econômico                    | Ônibus + barco                              |
| Casal em lua de mel / experiência premium | Trem Serra Verde Express                    |
| Grupo de amigos sem carro                 | Tour organizado                             |
| Quem quer ficar vários dias               | Carro ou ônibus (mais liberdade de horário) |

---

## Checklist para a Viagem de Curitiba

Antes de sair, confirme:

- [ ] Passagem de barco comprada (ou dinheiro em espécie para comprar no embarque)
- [ ] Estacionamento reservado em Pontal do Sul (alta temporada lota)
- [ ] Pousada na ilha reservada (obrigatório se for pernoitar)
- [ ] Dinheiro em espécie (a ilha não tem caixas eletrônicos)
- [ ] Repelente, lanterna, protetor solar e medicamentos básicos (não há farmácia na ilha)
- [ ] Roupas para chuva (o litoral paranaense é muito úmido)

Saindo de Curitiba com esse checklist, a viagem vai fluir sem surpresas.

Confira tudo que fazer depois de chegar no Guia Completo da [Ilha do Mel](/).

---

<PromptAlignedFAQ
  faqs={[
    {
      q: 'Como ir de Curitiba para a Ilha do Mel de ônibus?',
      a: 'Pegue um ônibus na Rodoferroviária de Curitiba com destino a Paranaguá (empresas Graciosa ou São José, ~1h30, ~R$ 35). De Paranaguá, pegue a barca para a Ilha do Mel (saídas às 15h30 nos dias de semana e 9h30 nos fins de semana, ~1h45 de travessia, ~R$ 76,50 ida e volta).',
    },
    {
      q: 'Quanto tempo leva ir de Curitiba para a Ilha do Mel?',
      a: 'De carro pela BR-277 até Pontal do Sul são 1h30 a 1h45, mais 30–35 min de barco: total de ~2h15 a 2h30. De ônibus até Paranaguá mais barco, o total fica em ~3h30. De trem pela Serra Verde Express, o percurso leva ~4h (trem) + 1h45 (barco): total de ~6h, mas é uma experiência turística completa.',
    },
    {
      q: 'O trem de Curitiba chega na Ilha do Mel?',
      a: 'O trem Serra Verde Express não vai até a ilha — ele vai até Paranaguá. De Paranaguá, você embarca na barca para a Ilha do Mel (1h45 de travessia). É um dos roteiros mais bonitos para chegar ao litoral paranaense.',
    },
    {
      q: 'É possível fazer a Ilha do Mel em 1 dia saindo de Curitiba?',
      a: 'Sim, mas o dia fica corrido. Saindo cedo de Curitiba (~6h), você chega a Pontal do Sul por volta das 8h, embarca no barco das 8h ou 8h30 e chega à ilha às 9h. Você tem a manhã e tarde para explorar e volta no barco das 16h ou 17h, chegando a Curitiba perto das 20h. Recomenda-se ficar pelo menos 2 noites para aproveitar melhor.',
    },
    {
      q: 'Qual a empresa de ônibus de Curitiba para Paranaguá?',
      a: 'As principais empresas são a Viação Graciosa e a Auto Viação São José. As saídas partem do Terminal Rodoviário de Curitiba (Rodoferroviária), na Av. Presidente Affonso Camargo. Consulte horários e compre passagens pelo site Clickbus ou diretamente na rodoviária.',
    },
    {
      q: 'Tem estacionamento seguro em Pontal do Sul para deixar o carro?',
      a: 'Sim. Há diversos estacionamentos pagos próximos ao terminal de embarque em Pontal do Sul, com diárias de R$ 25 a R$ 40. Na alta temporada (dezembro–fevereiro e feriados), recomenda-se reservar com antecedência. Alguns oferecem serviço de leva e traz até o terminal.',
    },
  ]}
/>
